import { toast } from '@/components/ui/use-toast';
import {
  MAX_INACTIVE_SECONDS,
  TIMER_SESSION_KEY,
  TIMER_VISIBILITY_KEY,
  endAndSaveSession,
  formatTime as formatTimeUtil,
  saveTimerVisibility
} from '@/utils/timer-utils';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

interface TimerContextType {
  isTimerRunning: boolean;
  sessionSeconds: number;
  sessionIntentionallyEnded: boolean;
  isTimerVisible: boolean;
  startSession: (projectId: string | number) => void;
  pauseSession: () => void;
  resumeSession: () => void;
  endSession: (projectId: string | number) => Promise<void>;
  hideTimer: () => void;
  showTimer: () => void;
  formatTime: (totalSeconds: number) => string;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Timer state
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [sessionTimerId, setSessionTimerId] = useState<NodeJS.Timeout | null>(null);
  const [sessionIntentionallyEnded, setSessionIntentionallyEnded] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | number | null>(null);

  // UI state
  const [isTimerVisible, setIsTimerVisible] = useState(() => {
    // Initialize from localStorage or default to true
    try {
      const savedVisibility = localStorage.getItem(TIMER_VISIBILITY_KEY);
      const initialVisibility = savedVisibility ? JSON.parse(savedVisibility) : true;
      console.log('Initial timer visibility:', initialVisibility);
      return initialVisibility;
    } catch (e) {
      console.error('Error reading timer visibility from localStorage:', e);
      return true;
    }
  });

  // Log visibility changes
  useEffect(() => {
    console.log('Timer visibility state changed:', isTimerVisible);
  }, [isTimerVisible]);

  // Debug: Track isTimerRunning changes
  useEffect(() => {
    console.log('🔴🟢 isTimerRunning changed to:', isTimerRunning, {
      sessionSeconds,
      sessionIntentionallyEnded,
      currentProjectId,
      sessionTimerId: !!sessionTimerId,
      timestamp: new Date().toISOString()
    });

    // Check if timer is running but shouldn't be
    if (isTimerRunning && sessionIntentionallyEnded) {
      console.error('🚨 BUG DETECTED: Timer is running but session was intentionally ended!');
    }
  }, [isTimerRunning, sessionSeconds, sessionIntentionallyEnded, currentProjectId, sessionTimerId]);

  // Use the formatTime utility
  const formatTime = formatTimeUtil;

  // Start a new writing session
  const startSession = (projectId: string | number) => {
    console.log('🟢 STARTING SESSION - START', {
      projectId,
      currentState: {
        sessionSeconds,
        isTimerRunning,
        sessionIntentionallyEnded,
        currentProjectId,
        sessionTimerId: !!sessionTimerId
      }
    });

    // Clear any existing timer first
    if (sessionTimerId) {
      console.log('🟢 Clearing existing timer');
      clearInterval(sessionTimerId);
      setSessionTimerId(null);
    }

    // Reset session state completely
    console.log('🟢 Resetting session state');
    setSessionSeconds(0);
    setIsTimerRunning(true);
    setSessionIntentionallyEnded(false);
    setCurrentProjectId(projectId);

    // Start timer
    console.log('🟢 Starting new timer');
    const timerId = setInterval(() => {
      setSessionSeconds(prev => {
        const newValue = prev + 1;

        // Save session duration to localStorage on each tick
        try {
          const currentSession = JSON.parse(localStorage.getItem(TIMER_SESSION_KEY) || '{}');
          localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify({
            ...currentSession,
            duration: newValue,
            lastActiveTime: new Date().toISOString(),
            projectId
          }));
        } catch (e) {
          console.error('Error saving session duration:', e);
        }

        return newValue;
      });
    }, 1000);

    setSessionTimerId(timerId);

    // Initialize session in localStorage
    const sessionData = {
      startTime: new Date().toISOString(),
      lastActiveTime: new Date().toISOString(),
      duration: 0,
      isActive: true,
      projectId
    };

    console.log('🟢 Saving session to localStorage:', sessionData);
    localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(sessionData));

    // Show a toast notification
    toast({
      description: 'Writing session started!',
    });

    // Make sure timer is visible when starting
    showTimer();

    console.log('🟢 STARTING SESSION - COMPLETE');
  };

  // Show/hide timer functions
  const showTimer = () => {
    console.log('showTimer called, current visibility:', isTimerVisible);
    setIsTimerVisible(true);
    saveTimerVisibility(true);
  };

  const hideTimer = () => {
    console.log('hideTimer called, current visibility:', isTimerVisible);
    setIsTimerVisible(false);
    saveTimerVisibility(false);
    console.log('Timer visibility set to false');
  };

  // Resume the current session
  const resumeSession = () => {
    // Only resume if we're not already running
    if (isTimerRunning) {
      console.log('Timer is already running, not resuming');
      return;
    }

    // Clear any existing timer to be safe
    if (sessionTimerId) {
      console.log('Clearing existing timer before resuming');
      clearInterval(sessionTimerId);
      setSessionTimerId(null);
    }

    console.log('Resuming timer from', sessionSeconds, 'seconds');

    // Start a new timer with a ref to the current time to ensure accurate timing
    const startTime = Date.now() - (sessionSeconds * 1000);

    const timerId = setInterval(() => {
      // Calculate elapsed time based on the actual time difference
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);

      setSessionSeconds(elapsedSeconds);

      // Save session duration to localStorage on each tick
      try {
        const currentSession = JSON.parse(localStorage.getItem(TIMER_SESSION_KEY) || '{}');
        localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify({
          ...currentSession,
          duration: elapsedSeconds,
          lastActiveTime: new Date().toISOString(),
        }));
      } catch (e) {
        console.error('Error saving session duration:', e);
      }
    }, 1000);

    setSessionTimerId(timerId);
    setIsTimerRunning(true);

    // Mark session as active in localStorage
    try {
      const savedSession = localStorage.getItem(TIMER_SESSION_KEY);
      if (savedSession) {
        const session = JSON.parse(savedSession);
        session.isActive = true;
        localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(session));
      }
    } catch (e) {
      console.error('Error resuming session:', e);
    }

    // Make sure timer is visible when resuming
    showTimer();
  };

  // Pause the current session
  const pauseSession = () => {
    // Only pause if we're actually running
    if (!isTimerRunning) {
      console.log('Timer is not running, nothing to pause');
      return;
    }

    console.log('Pausing timer at', sessionSeconds, 'seconds');

    // Clear timer
    if (sessionTimerId) {
      clearInterval(sessionTimerId);
      setSessionTimerId(null);
    } else {
      console.warn('No timer ID found when pausing');
    }

    setIsTimerRunning(false);

    // Mark session as inactive in localStorage
    try {
      const savedSession = localStorage.getItem(TIMER_SESSION_KEY);
      if (savedSession) {
        const session = JSON.parse(savedSession);
        session.isActive = false;
        localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(session));
      }
    } catch (e) {
      console.error('Error pausing session:', e);
    }

    toast({
      description: 'Session paused.',
    });
  };

  // End the current session and save it
  const endSession = async (projectId: string | number) => {
    console.log('🔴 ENDING SESSION - START', {
      sessionSeconds,
      isTimerRunning,
      sessionTimerId: !!sessionTimerId,
      currentProjectId,
      sessionIntentionallyEnded
    });

    // Store the current session seconds before clearing
    const currentSessionSeconds = sessionSeconds;

    // Step 1: Clear timer immediately and completely
    if (sessionTimerId) {
      console.log('🔴 Clearing timer interval:', sessionTimerId);
      clearInterval(sessionTimerId);
      console.log('🔴 Timer interval cleared');
      setSessionTimerId(null);
      console.log('🔴 Timer ID set to null');
    } else {
      console.warn('🔴 No timer ID found when ending session - this might be the problem!');
    }

    // Step 2: Reset ALL timer states immediately using flushSync for synchronous updates
    console.log('🔴 Resetting timer states - BEFORE:', {
      isTimerRunning,
      sessionSeconds,
      currentProjectId,
      sessionIntentionallyEnded
    });

    // Use flushSync to force synchronous state updates
    flushSync(() => {
      setIsTimerRunning(false);
      setSessionSeconds(0);
      setCurrentProjectId(null);
      setSessionIntentionallyEnded(true);
    });

    console.log('🔴 State setters completed with flushSync - AFTER:', {
      isTimerRunning: false, // This should be false now
      sessionSeconds: 0,
      currentProjectId: null,
      sessionIntentionallyEnded: true
    });

    // Step 3: Hide the timer widget
    console.log('🔴 Hiding timer widget');
    hideTimer();

    // Step 4: Save session to database
    console.log('🔴 Saving session to database with duration:', currentSessionSeconds);
    try {
      await endAndSaveSession(projectId, currentSessionSeconds);
      console.log('🔴 Session saved successfully');
    } catch (error) {
      console.error('🔴 Error saving session:', error);
    }

    // Step 5: Final state verification
    console.log('🔴 ENDING SESSION - COMPLETE', {
      sessionSeconds: 0,
      isTimerRunning: false,
      sessionTimerId: null,
      currentProjectId: null,
      sessionIntentionallyEnded: true
    });
  };

  // Cleanup and save session state when navigating away
  useEffect(() => {
    // Save session state before navigating away
    const handleBeforeUnload = () => {
      // Only save the session if it's running and not intentionally ended
      if (isTimerRunning && !sessionIntentionallyEnded) {
        console.log('Saving session state before navigation');

        // Save current time as last active time
        try {
          const savedSession = JSON.parse(localStorage.getItem(TIMER_SESSION_KEY) || '{}');
          savedSession.lastActiveTime = new Date().toISOString();
          savedSession.isActive = true;
          localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(savedSession));
        } catch (e) {
          console.error('Error saving session before navigation:', e);
        }
      }
    };

    // Add event listener for navigation
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTimerRunning, sessionIntentionallyEnded]);

  // Restore session on initial load
  useEffect(() => {
    console.log('🟡 INITIALIZING TIMER CONTEXT', {
      sessionIntentionallyEnded,
      sessionSeconds,
      isTimerRunning
    });

    // Clear any existing timers first to prevent duplicates
    if (sessionTimerId) {
      console.log('🟡 Clearing existing timer on init');
      clearInterval(sessionTimerId);
      setSessionTimerId(null);
    }

    // If session was intentionally ended, don't restore anything
    if (sessionIntentionallyEnded) {
      console.log('🟡 Session was intentionally ended, not restoring');
      localStorage.removeItem(TIMER_SESSION_KEY);
      return;
    }

    // Get any existing timer session
    const savedSession = localStorage.getItem(TIMER_SESSION_KEY);

    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        console.log('🟡 Found saved session:', session);

        // Calculate time elapsed since the session was last active
        const lastActiveTime = new Date(session.lastActiveTime);
        const now = new Date();
        const timeElapsedMs = now.getTime() - lastActiveTime.getTime();
        const timeElapsedSeconds = Math.floor(timeElapsedMs / 1000);

        console.log(`🟡 Session found: last active ${Math.floor(timeElapsedSeconds / 60)} minutes ago`);

        // Check if session was intentionally ended or has expired
        const hasExpired = timeElapsedSeconds > MAX_INACTIVE_SECONDS;

        if (session.isActive && !hasExpired) {
          // Resume session
          console.log('🟡 Restoring active session...');

          // Cap time update at 1 hour max
          const timeToAdd = Math.min(timeElapsedSeconds, 3600);

          // Show toast notification about session restoration
          toast({
            description: 'Your writing session has been restored!',
          });

          // Set session data
          setSessionSeconds(session.duration + timeToAdd);
          setIsTimerRunning(true);
          setCurrentProjectId(session.projectId);
          setSessionIntentionallyEnded(false);

          // Start timer
          const timerId = setInterval(() => {
            setSessionSeconds(prev => prev + 1);
          }, 1000);

          setSessionTimerId(timerId);

          // Update lastActiveTime
          session.lastActiveTime = now.toISOString();
          session.duration += timeToAdd;
          localStorage.setItem(TIMER_SESSION_KEY, JSON.stringify(session));
        } else if (hasExpired) {
          console.log('🟡 Session has expired (inactive for too long)');
          localStorage.removeItem(TIMER_SESSION_KEY);
        } else {
          console.log('🟡 Session exists but is not active (paused)');
          setSessionSeconds(session.duration);
          setCurrentProjectId(session.projectId);
          setIsTimerRunning(false);
          setSessionIntentionallyEnded(false);
        }
      } catch (e) {
        console.error('🟡 Error restoring session:', e);
        // Clean up potentially corrupted data
        localStorage.removeItem(TIMER_SESSION_KEY);
      }
    } else {
      console.log('🟡 No saved session found');
    }

    // Cleanup function to handle component unmount
    return () => {
      if (sessionTimerId) {
        console.log('🟡 Cleaning up timer on unmount');
        clearInterval(sessionTimerId);
      }
    };
  }, []);

  return (
    <TimerContext.Provider
      value={{
        isTimerRunning,
        sessionSeconds,
        sessionIntentionallyEnded,
        isTimerVisible,
        startSession,
        pauseSession,
        resumeSession,
        endSession,
        hideTimer,
        showTimer,
        formatTime,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
